/**
 * /practice/why — User-facing plan explanation.
 *
 * Translates the coach algorithm decisions into plain language.
 * Linked from the practice hub when the user has review history.
 */

import { type ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  buildCoachPlanWithTrace,
  type StrengthenTrace,
  type ReviewTrace,
  type IntroduceTrace,
} from "@music-app/learning-system";
import { usePracticeData } from "../lib/use-review-data.js";

// ─── page ─────────────────────────────────────────────────────────────────────

export function ReviewWhyPage(): ReactNode {
  const {
    cards,
    cardsLoaded,
    items,
    thresholds,
    reviews,
    prefsLoaded,
    sessionTargetMinutes,
    nodeExposures,
    activePlanTemplate,
    activePlan,
  } = usePracticeData();

  const today = useMemo(() => new Date(), []);

  const { trace } = useMemo(
    () =>
      buildCoachPlanWithTrace(activePlanTemplate, {
        items,
        cards,
        thresholds,
        reviews,
        sessionTargetMinutes,
        sessionMix: activePlan.sessionMix,
        newSkillNodeId: activePlan.focusNodeIds?.[0] ?? null,
        nodeExposures,
        now: today,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, cards, thresholds, reviews, sessionTargetMinutes, activePlan, nodeExposures, today, activePlanTemplate],
  );

  if (!cardsLoaded || !prefsLoaded) {
    return (
      <div className="max-w-2xl mx-auto py-9 px-8">
        <p className="font-mono text-sm text-inkFaded">Loading…</p>
      </div>
    );
  }

  const activeStopKinds = new Set(trace.finalStops.map((s) => s.kind));

  const resolvedLabel =
    trace.resolvedTemplateId !== trace.templateId
      ? `${trace.templateLabel} → ${trace.resolvedTemplateLabel}`
      : trace.templateLabel;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 space-y-6">

      {/* ── header ── */}
      <div>
        <Link
          to="/review"
          className="font-mono text-[10px] text-inkFaded hover:text-ink transition-colors"
        >
          ← back to plan
        </Link>
        <h1 className="font-serif text-2xl font-medium text-ink mt-2">Why today?</h1>
        <p className="font-mono text-[11px] text-inkFaded mt-1">
          {resolvedLabel} plan · {sessionTargetMinutes} min
        </p>
      </div>

      {/* ── intervention banner ── */}
      {trace.mode === "intervention" && trace.interventionNodeTitle && (
        <div className="border border-warning/40 bg-warning/5 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-warning mb-1.5">
            Focus session
          </p>
          <p className="font-serif text-sm text-ink leading-snug">
            Your error rate on <strong>{trace.interventionNodeTitle}</strong> hit a critical
            level this week. Today&apos;s session focuses entirely on bringing it back down —
            new material is paused until recall improves.
          </p>
        </div>
      )}

      {/* ── no stops ── */}
      {trace.finalStops.length === 0 && (
        <p className="font-serif italic text-inkSoft">
          Nothing is scheduled right now — you&apos;re all caught up.
        </p>
      )}

      {/* ── stop cards ── */}
      {activeStopKinds.has("strengthen") && (
        <StrengthenCard trace={trace.strengthenTrace} />
      )}
      {activeStopKinds.has("review") && (
        <ReviewCard trace={trace.reviewTrace} />
      )}
      {activeStopKinds.has("introduce") && (
        <IntroduceCard trace={trace.introduceTrace} />
      )}

      {/* ── footer ── */}
      <div className="border-t border-ink/10 pt-4 space-y-2">
        <p className="font-mono text-[10px] text-inkFaded leading-relaxed">
          The schedule is rebuilt each day using spaced repetition (FSRS).
          Items are chosen to maximise long-term retention within the time you have available.
        </p>
        {import.meta.env.DEV && (
          <Link
            to="/review/explain"
            className="font-mono text-[10px] text-inkFaded hover:text-ink transition-colors"
          >
            See raw algorithm trace →
          </Link>
        )}
      </div>

    </div>
  );
}

// ─── StrengthenCard ───────────────────────────────────────────────────────────

function StrengthenCard({ trace }: { trace: StrengthenTrace }): ReactNode {
  const itemCount = trace.selectedItems.length;

  return (
    <ExplainCard kind="strengthen" label="Strengthen" itemCount={itemCount}>
      {trace.mode === "high-lapse" && trace.winnerNodeTitle ? (
        <>
          <p className="font-serif text-sm text-ink leading-snug">
            You&apos;ve been missing <strong>{trace.winnerNodeTitle}</strong> frequently —{" "}
            {Math.round(trace.winnerLapseRate * 100)}% of your attempts in the past{" "}
            {trace.lapseWindowDays} days. These items target the weakest ones first.
          </p>
          {trace.relistenNudge && (
            <p className="font-serif italic text-sm text-inkSoft mt-2 leading-snug">
              Your error rate is high enough that re-listening to examples may help more
              than drilling right now.
            </p>
          )}
        </>
      ) : trace.mode === "weakest-items" ? (
        <p className="font-serif text-sm text-ink leading-snug">
          No single skill had a high error rate recently. These items were chosen
          for their low stability — the memories most at risk of fading without a refresher.
        </p>
      ) : null}
    </ExplainCard>
  );
}

// ─── ReviewCard ───────────────────────────────────────────────────────────────

function ReviewCard({ trace }: { trace: ReviewTrace }): ReactNode {
  const { dueCountTotal, dueCountSelected, topNodeBreakdown } = trace;

  return (
    <ExplainCard kind="review" label="Review" itemCount={dueCountSelected}>
      <p className="font-serif text-sm text-ink leading-snug">
        {dueCountTotal === dueCountSelected ? (
          <>All {dueCountSelected} due items are included today.</>
        ) : (
          <>
            {dueCountTotal} items are due today.{" "}
            {dueCountSelected} are in today&apos;s session — those with the lowest current recall,
            at highest risk of being forgotten. The rest carry over.
          </>
        )}
      </p>
      {topNodeBreakdown.length > 0 && (
        <p className="font-mono text-[10px] text-inkFaded mt-2">
          {"From: "}
          {topNodeBreakdown
            .slice(0, 4)
            .map((n) => `${n.title} (${n.count})`)
            .join(" · ")}
          {topNodeBreakdown.length > 4 && ` · +${topNodeBreakdown.length - 4} more`}
        </p>
      )}
    </ExplainCard>
  );
}

// ─── IntroduceCard ────────────────────────────────────────────────────────────

function IntroduceCard({ trace }: { trace: IntroduceTrace }): ReactNode {
  const { primaryNodeTitle, secondNodeTitle, selectedItemCount, listenFirst, nodeAcquisition } =
    trace;

  const gatedNodes = nodeAcquisition.filter((a) => a.status === "needs-listen");

  return (
    <ExplainCard kind="introduce" label="Introduce" itemCount={selectedItemCount}>
      {listenFirst && gatedNodes.length > 0 ? (
        <p className="font-serif text-sm text-ink leading-snug">
          Before drilling{primaryNodeTitle ? <> <strong>{primaryNodeTitle}</strong></> : ""}, you
          need to hear{" "}
          {gatedNodes.length === 1 && gatedNodes[0] !== undefined ? (
            <>
              {Math.max(1, gatedNodes[0].threshold - gatedNodes[0].exposures)} more{" "}
              {Math.max(1, gatedNodes[0].threshold - gatedNodes[0].exposures) === 1
                ? "example"
                : "examples"}
            </>
          ) : (
            <>more examples</>
          )}{" "}
          to build familiarity. Listen is scheduled first.
        </p>
      ) : primaryNodeTitle ? (
        <p className="font-serif text-sm text-ink leading-snug">
          You&apos;ve heard enough of <strong>{primaryNodeTitle}</strong> to start drilling.{" "}
          {secondNodeTitle && (
            <>
              A few items from <strong>{secondNodeTitle}</strong> are included as well.{" "}
            </>
          )}
          {selectedItemCount} new item{selectedItemCount !== 1 ? "s" : ""} added.
        </p>
      ) : (
        <p className="font-serif italic text-sm text-inkSoft leading-snug">
          No new items available right now.
        </p>
      )}

      {gatedNodes.length > 0 && (
        <div className="mt-3 space-y-2">
          {gatedNodes.map((acq) => {
            const fillPct = Math.min(100, Math.round((acq.exposures / acq.threshold) * 100));
            return (
              <div key={acq.nodeId} className="flex items-center gap-3">
                <span className="font-mono text-[10px] text-inkFaded w-40 truncate shrink-0">
                  {acq.title}
                </span>
                <div className="h-0.5 bg-ink/10 flex-1 overflow-hidden">
                  <div className="h-full bg-success" style={{ width: `${fillPct}%` }} />
                </div>
                <span className="font-mono text-[9px] text-inkFaded tabular-nums shrink-0">
                  {acq.exposures}/{acq.threshold} listens
                </span>
              </div>
            );
          })}
        </div>
      )}
    </ExplainCard>
  );
}

// ─── ExplainCard ──────────────────────────────────────────────────────────────

const CARD_LEFT_RULE: Record<string, string> = {
  strengthen: "border-l-4 border-l-warning",
  review: "border-l-4 border-l-info",
  introduce: "border-l-4 border-l-success",
};

const CARD_LABEL_COLOR: Record<string, string> = {
  strengthen: "text-warning",
  review: "text-info",
  introduce: "text-success",
};

function ExplainCard({
  kind,
  label,
  itemCount,
  children,
}: {
  kind: string;
  label: string;
  itemCount: number;
  children: ReactNode;
}): ReactNode {
  return (
    <div
      className={`bg-paperWarm border border-ink/10 px-4 py-4 ${CARD_LEFT_RULE[kind] ?? ""}`}
    >
      <div className="flex items-baseline justify-between mb-2">
        <span
          className={`font-mono text-[10px] uppercase tracking-[0.18em] font-medium ${CARD_LABEL_COLOR[kind] ?? "text-ink"}`}
        >
          {label}
        </span>
        <span className="font-mono text-[10px] text-inkFaded tabular-nums">
          {itemCount} item{itemCount !== 1 ? "s" : ""}
        </span>
      </div>
      {children}
    </div>
  );
}
